import React, { useRef, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ClipboardList, BadgeCheck, ListChecks, ShieldAlert } from "lucide-react";
import { useBorrowerDetail } from "@/hooks/useBorrowerDetail";
import { useAgentPageState } from "@/agent/pageState/registry";
import IntakeStatusBanner from "@/components/application/IntakeStatusBanner";
import BorrowerNavHeader from "@/components/application/ApplicationNavHeader";
import { BrokerAppStatusFlowbar } from "@/components/borrower/BrokerAppStatusFlowbar";
import DetailPageSkeleton from "@/components/common/DetailPageSkeleton";
import AppDetailTabs, { TabConfig } from "@/components/layout/AppDetailTabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
// "AI Analysis" is the effective default tab (mounts immediately on first
// visit, before the status-based redirect effect below fires), so it stays
// a static import — lazy-loading it would add a chunk-fetch waterfall to
// first paint. Every other tab is code-split: its JS chunk is only fetched
// once the user actually opens the tab (AppDetailTabs wraps panels in
// <Suspense>).
import { latestAnalysis } from "@/components/application/finalUnderwritingDisplay";
import {
  hasAnalysisListLink,
  linkedTab,
  withoutAnalysisListLink,
} from "@/components/application/aiAnalysis/analysisListLink";
import FunderAnalysisDashboard from "@/components/underwriting/FunderAnalysisDashboard";
// Also statically imported elsewhere (App.tsx, AdminAppDetail.tsx), so
// lazy-wrapping it here wouldn't actually split it into its own chunk.
import BorrowerForm from "./BorrowerForm";
import { lazyWithChunkReload } from "@/utils/lazyWithChunkReload";

const DefaultHistoryTab = lazyWithChunkReload(
  () => import("@/components/application/DefaultHistoryTab")
);
const BrokerUnderwritingDetailTab = lazyWithChunkReload(
  () => import("@/components/application/BrokerUnderwritingDetailTab")
);
const MerchantTasksTab = lazyWithChunkReload(
  () => import("@/components/application/merchant-tasks/MerchantTasksTab")
);

/** Every key this page can render. A stored key outside it (e.g. the removed
 *  `contracts` or `fraud-detection` tab) falls back to the default. */
const BROKER_APP_TAB_KEYS = [
  "application",
  "default-history",
  "ai-analysis",
  "underwriting-detail",
  "merchant-tasks",
] as const;
type BrokerAppTabKey = (typeof BROKER_APP_TAB_KEYS)[number];

/** One-shot deep-link seed (e.g. Default History column click). Not updated on tab clicks. */
const BROKER_APP_DETAIL_TAB_STORAGE_KEY = "broker_app_detail_active_tab_v2";

function defaultBrokerAppTabForStatus(status: string | null | undefined): BrokerAppTabKey {
  if (status === "submitted" || status === "ai_analyzing" || status === "funder_review") {
    return "default-history";
  }
  return "underwriting-detail";
}

/** Given the deal's status, a tab seeded at another status is stale (the deal moved on since). */
function readSeededBrokerAppTab(
  id: string | undefined,
  status?: string | null
): BrokerAppTabKey | null {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(BROKER_APP_DETAIL_TAB_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { id?: string; tab?: string; status?: string };
    if (stored.id !== id || typeof stored.tab !== "string") return null;
    if (status != null && stored.status != null && stored.status !== status) return null;
    if (!(BROKER_APP_TAB_KEYS as readonly string[]).includes(stored.tab)) return null;
    return stored.tab as BrokerAppTabKey;
  } catch {
    return null;
  }
}

const BrokerAppDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [multiFunderCreatedCount, setMultiFunderCreatedCount] = useState<number | null>(null);
  const borrowerId = id ? parseInt(id, 10) : null;
  const { borrower, loading, error, refresh } = useBorrowerDetail(Number(id));
  // A link's ?tab= wins over the landing below.
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlTab = linkedTab(searchParams, BROKER_APP_TAB_KEYS);
  const [activeTab, setActiveTab] = useState<BrokerAppTabKey>(
    () => urlTab ?? readSeededBrokerAppTab(id) ?? "ai-analysis"
  );
  useAgentPageState("tab", { tab: activeTab });
  // Last status we applied a default for — re-default when the deal or status changes.
  const appliedStatusRef = useRef<{ id: string; status: string } | null>(null);
  // Statements lane only — Plaid decisions belong on Final Underwriting.
  const statementsAnalysis = latestAnalysis(borrower, "statements");

  useEffect(() => {
    const count = (location.state as { multiFunderCreatedCount?: number } | null)
      ?.multiFunderCreatedCount;
    if (typeof count === "number" && count > 1) {
      setMultiFunderCreatedCount(count);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!borrower || !id) return;
    const status = borrower.app_status;
    const prev = appliedStatusRef.current;
    if (prev?.id === id && prev.status === status) return;

    const isStatusChange = prev?.id === id && prev.status !== status;
    appliedStatusRef.current = { id, status };

    if (isStatusChange) {
      setActiveTab(defaultBrokerAppTabForStatus(status));
      return;
    }

    const seeded = readSeededBrokerAppTab(id, status);
    setActiveTab(urlTab ?? seeded ?? defaultBrokerAppTabForStatus(status));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the URL tab is read at landing; the effect below follows it after
  }, [borrower, id]);

  // A link opened on this page (in place) switches the tab, again each time it is opened.
  useEffect(() => {
    if (urlTab) setActiveTab(urlTab);
  }, [urlTab, location.key]);

  // A tab picked by hand ends the link, so a reload does not reopen a view no longer on screen.
  const changeTab = (tab: BrokerAppTabKey) => {
    setActiveTab(tab);
    if (tab !== activeTab && hasAnalysisListLink(searchParams)) {
      navigate(
        { search: withoutAnalysisListLink(searchParams, true).toString() },
        { replace: true, preventScrollReset: true }
      );
    }
  };

  const tabs: TabConfig[] = [
    {
      key: "application",
      label: "Application",
      icon: <ClipboardList className="h-4 w-4" />,
      panel: (
        <div className="px-3">
          <BorrowerForm embedded />
        </div>
      ),
    },
    {
      key: "default-history",
      label: "Background Check",
      icon: <ShieldAlert className="h-4 w-4" />,
      panel: borrower ? <DefaultHistoryTab applicationId={borrower.id} /> : null,
    },
    {
      key: "ai-analysis",
      label: "AI Analysis",
      icon: <ClipboardList className="h-4 w-4" />,
      panel: (
        <div className="px-3">
          {statementsAnalysis ? (
            <FunderAnalysisDashboard
              analysis={statementsAnalysis}
              borrowerId={borrowerId ?? undefined}
              readOnly // <= BROKER VIEW: cannot change offer/decision
            />
          ) : (
            <Alert className="mt-4">
              <AlertDescription>
                No underwriting analysis available yet. Once statements are processed, the AI
                analysis will appear here.
              </AlertDescription>
            </Alert>
          )}
        </div>
      ),
    },
    {
      key: "underwriting-detail",
      label: "Underwriting Detail",
      icon: <BadgeCheck className="h-4 w-4" />,
      panel: borrower ? <BrokerUnderwritingDetailTab applicationId={borrower.id} /> : null,
    },
    {
      key: "merchant-tasks",
      label: "Merchant Tasks",
      icon: <ListChecks className="h-4 w-4" />,
      panel: borrower ? <MerchantTasksTab borrower={borrower} /> : null,
    },
  ];

  // Loading state
  if (loading && !borrower) {
    return <DetailPageSkeleton showFlowbar={true} tabCount={5} />;
  }

  // Error state
  if (error) {
    return (
      <div>
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          onClick={() => navigate("/applications")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
      </div>
    );
  }

  // Not found state
  if (!borrower) {
    return (
      <div>
        <Alert className="mb-4 border-amber-500/50 bg-amber-50 text-amber-950 dark:bg-amber-950/20 dark:text-amber-100">
          <AlertDescription>Application not found</AlertDescription>
        </Alert>
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          onClick={() => navigate("/applications")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
      </div>
    );
  }

  return (
    <div>
      <BorrowerNavHeader
        companyName={borrower.company_name}
        ownerName={borrower.owner_name}
        organizationName={borrower.organization_name}
      />

      {multiFunderCreatedCount != null && (
        <Alert className="mb-3">
          <AlertDescription>
            Created {multiFunderCreatedCount} applications (one per selected funder). You are
            viewing the first; switch organizations to see the others.
          </AlertDescription>
        </Alert>
      )}

      <BrokerAppStatusFlowbar borrowerId={borrower.id} />

      <div className="my-3">
        <IntakeStatusBanner borrower={borrower} onChanged={refresh} />
      </div>

      <AppDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(v) => changeTab(v as BrokerAppTabKey)}
      />
    </div>
  );
};

export default BrokerAppDetail;
